// Your settings › Voice (`/c/{slug}/setup/voice`; VOICE-CHOICES). Every member opens it:
//   - Your voice: the stock voices offered now (the voice service's list, so a premium voice is
//     absent until its keys exist), each with a preview of its sample line and a short price
//     note ("included", "$0.03 per minute"). Saved to `me/prefs/voice_front.voice_id`.
//   - Mood: Plain (default, fastest: no speaking instructions) or Warm, Energetic, Calm, Serious,
//     each with its speed indicator; leaving Plain shows "Replies will take a little longer in
//     this mood." Saved to `me/prefs/voice_front.mood`.
//   - Company default (owners, admins, the agency): the voice a person who picked none talks to
//     (`voice.settings.put`). Also listed under company Setup.
// Voice itself stays behind the company's `voice_front` flag; text mode needs none of this.
import type { OperationOutput } from "@companyos/sdk";
import { Badge, Button, Notice, SettingsGroup, SettingsRow } from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AudioLinesIcon, PlayIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "./problem-text";
import { SetupPage } from "./setup-page";

type Settings = OperationOutput<"voice.settings.get">;
type Offered = { id: string; name: string; tier: string; engine: string };

const voiceBase = (): string => {
  const v = (import.meta as { env?: Record<string, unknown> }).env?.VITE_VOICE_URL;
  return (typeof v === "string" && v ? v : "/voice").replace(/\/$/, "");
};

export const voiceSettingsKey = (co: string) => ["company", co, "voice-settings"] as const;
const settingsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: voiceSettingsKey(co),
    queryFn: () => api.client.voice.settings.get({ params: { co } }),
  });

/** The voices the voice service can speak now (null: the service did not answer). */
export async function offeredVoices(f: typeof fetch = fetch): Promise<Offered[] | null> {
  try {
    const r = await f(`${voiceBase()}/v1/voices`, { credentials: "include" });
    if (!r.ok) return null;
    return ((await r.json()) as { voices: Offered[] }).voices;
  } catch {
    return null;
  }
}

export function VoiceSettingsScreen({
  api,
  company,
  fetchImpl,
}: {
  api: Api;
  company: CompanyAccess;
  fetchImpl?: typeof fetch;
}) {
  const q = useQuery(settingsQuery(api, company.id));
  const offered = useQuery({
    queryKey: ["voice-service", "voices"],
    queryFn: () => offeredVoices(fetchImpl),
    staleTime: 60_000,
  });
  return (
    <SetupPage title="Voice" icon={AudioLinesIcon} slot="setup-voice">
      {q.isPending || offered.isPending ? (
        <Loading label="Loading voices" rows={4} />
      ) : q.isError ? (
        <LoadError what="Voice" error={q.error} />
      ) : (
        <VoiceForm api={api} co={company.id} view={q.data} offered={offered.data ?? null} />
      )}
    </SetupPage>
  );
}

function VoiceForm({
  api,
  co,
  view,
  offered,
}: {
  api: Api;
  co: string;
  view: Settings;
  offered: Offered[] | null;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // Only voices the service offers now, priced by the API (a premium voice without keys is absent).
  const ids = new Set((offered ?? []).map((v) => v.id));
  const voices = view.voices.filter((v) => ids.has(v.id));
  const fallback = view.default_voice_id ?? voices[0]?.id ?? null;
  const mine = view.my_voice_id && ids.has(view.my_voice_id) ? view.my_voice_id : fallback;
  const savePref = useMutation({
    mutationFn: (value: { voice_id?: string; mood?: string }) =>
      api.client.me.prefs.put({
        params: { co, key: "voice_front" },
        body: { value: { ...(currentPref(view) as object), ...value } },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: voiceSettingsKey(co) }),
    onError: (e) => setError(problemText(e, "Your voice could not be saved.")),
  });
  const saveDefault = useMutation({
    mutationFn: (id: string | null) =>
      api.client.voice.settings.put({ params: { co }, body: { default_voice_id: id } }),
    onSuccess: (next) => qc.setQueryData(voiceSettingsKey(co), next),
    onError: (e) => setError(problemText(e, "The company default could not be saved.")),
  });
  return (
    <>
      {error ? (
        <Notice tone="danger" data-slot="voice-error">
          {error}
        </Notice>
      ) : null}
      {offered === null ? (
        <Notice data-slot="voice-unreachable">
          Voice is not set up here yet. Text mode works without it.
        </Notice>
      ) : null}
      <SettingsGroup id="your-voice" title="Your voice">
        {voices.length === 0 && offered !== null ? (
          <p className="text-muted-foreground text-sm">No voice is available yet.</p>
        ) : null}
        <fieldset data-slot="voice-picker" className="flex flex-col gap-1">
          <legend className="sr-only">Your voice</legend>
          {voices.map((v) => (
            <VoiceRow
              key={v.id}
              name="voice"
              voice={v}
              checked={mine === v.id}
              isDefault={fallback === v.id}
              onPick={() => savePref.mutate({ voice_id: v.id })}
            />
          ))}
        </fieldset>
      </SettingsGroup>
      <SettingsGroup id="mood" title="Mood">
        <fieldset data-slot="mood-picker" className="flex flex-col gap-1">
          <legend className="sr-only">Mood</legend>
          {view.moods.map((m) => (
            <label key={m.id} className="flex items-center gap-2 py-1" data-slot={`mood-${m.id}`}>
              <input
                type="radio"
                name="mood"
                checked={view.my_mood === m.id}
                onChange={() => savePref.mutate({ mood: m.id })}
              />
              <span className="font-medium">{m.label}</span>
              <span className="text-muted-foreground text-sm" data-slot="mood-speed">
                {m.speed}
              </span>
            </label>
          ))}
        </fieldset>
        {view.my_mood !== "plain" ? (
          <p className="text-muted-foreground text-sm" data-slot="mood-note">
            {view.mood_note}
          </p>
        ) : null}
      </SettingsGroup>
      {view.can_set_default ? (
        <SettingsGroup id="company-default" title="Company default">
          <SettingsRow
            label="Default voice"
            hint="Who people talk to until they pick their own voice."
          >
            <select
              aria-label="Company default voice"
              data-slot="company-default"
              value={view.default_voice_id ?? ""}
              onChange={(e) => saveDefault.mutate(e.target.value || null)}
            >
              <option value="">{voices[0] ? `${voices[0].name} (built in)` : "Built in"}</option>
              {voices.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} — {v.price_note}
                </option>
              ))}
            </select>
          </SettingsRow>
        </SettingsGroup>
      ) : null}
    </>
  );
}

const currentPref = (view: Settings) => ({
  ...(view.my_voice_id ? { voice_id: view.my_voice_id } : {}),
  ...(view.my_mood !== "plain" ? { mood: view.my_mood } : {}),
});

function VoiceRow({
  name,
  voice,
  checked,
  isDefault,
  onPick,
}: {
  name: string;
  voice: Settings["voices"][number];
  checked: boolean;
  isDefault: boolean;
  onPick: () => void;
}) {
  const play = () => {
    // The voice service makes the sample once per voice and caches it.
    void new Audio(`${voiceBase()}/v1/voices/${voice.id}/preview`).play().catch(() => undefined);
  };
  return (
    <div className="flex items-center gap-3 py-1" data-slot={`voice-${voice.id}`}>
      <label className="flex flex-1 items-center gap-2">
        <input type="radio" name={name} checked={checked} onChange={onPick} />
        <span className="font-medium">{voice.name}</span>
        {isDefault ? <Badge variant="neutral">Default</Badge> : null}
        <span className="text-muted-foreground text-sm" data-slot="price-note">
          {voice.price_note}
        </span>
      </label>
      <Button variant="ghost" size="sm" onClick={play} aria-label={`Play ${voice.name}`}>
        <PlayIcon /> Preview
      </Button>
    </div>
  );
}
